import { ApiMethod } from '../models/api-types.type';
import { useAuthContext } from './use-auth-context.hook';

/**
 * The vendor's own business profile: where they live on the internet and
 * what they have told us about the business.
 *
 * This is the source the setup-gap findings are computed from, so a write
 * here recomputes them on the server at once — the dashboard card that asked
 * for a website disappears on the next load, without a checklist to tick.
 */

export interface VendorSocials {
    instagram?: string;
    facebook?: string;
    pinterest?: string;
    tiktok?: string;
    linkedin?: string;
}

export interface VendorProfile {
    websiteUrl?: string;
    socials?: VendorSocials;
    competitors?: string[];
    markets?: string[];
    houseRules?: string;
    /** Workspaces chosen in onboarding, most important first. */
    goals?: string[];
    /** When the first-run journey was finished. */
    onboardedAt?: string;
    updatedAt?: string;
}

/** Partial: only the keys present are written. An empty string clears a field. */
export type VendorProfilePatch = Partial<Omit<VendorProfile, 'updatedAt' | 'onboardedAt'>> & {
    /** true marks the journey finished; the server stamps the time. */
    onboarded?: boolean;
};

const BASE = '/services/pms/vendors/me/profile';

export const useVendorProfileApi = () => {
    const { sendAuthGuardedRequest } = useAuthContext();

    const get = async (): Promise<VendorProfile> => {
        const response = await sendAuthGuardedRequest(ApiMethod.GET, BASE);
        return (response ?? {}) as VendorProfile;
    };

    const update = async (patch: VendorProfilePatch): Promise<VendorProfile> => {
        const response = await sendAuthGuardedRequest(ApiMethod.PATCH, BASE, patch);
        return (response ?? {}) as VendorProfile;
    };

    return { get, update };
};
